import { ApacCheckoutComponent, useApacCheckoutStyles } from "@apac/checkout";
import {
  useSelectPayment,
  CardDisplay,
  MerchandisingAlert,
} from "@apac/components/checkout";
import type { CreditCard } from "@b2bportal/card-api";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { Product } from "@b2bportal/purchase-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon, RadioGroup } from "@tenant/ui";
import defaultStyles from "./SelectPayment.module.scss";

export interface SelectPaymentProps {
  savedPayments: CreditCard[];
  selectedPaymentHopperId: string | undefined;
  disabled?: boolean;
  selectPaymentMethod: (paymentId: string | undefined) => void;
  handleClickAddPaymentMethod: () => void;
  product?: Product;
  /**
   * Predicate to decide if a saved card can be selected for payment. Cards
   * that fail this check are still listed (so the user can recognise them
   * and remove them from the wallet) but are rendered non-selectable.
   * Defaults to allowing every card.
   */
  isCardAllowed?: (card: CreditCard) => boolean;
}

export const SelectPayment: React.FC<SelectPaymentProps> = ({
  savedPayments,
  selectedPaymentHopperId,
  disabled,
  selectPaymentMethod,
  handleClickAddPaymentMethod,
  product,
  isCardAllowed,
}) => {
  const { t } = useI18nContext();
  const styles = useApacCheckoutStyles(
    ApacCheckoutComponent.SelectPayment,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, ApacCheckoutComponent.SelectPayment);

  const { cardPaymentDisabled, selectedPayment } = useSelectPayment({
    savedPayments,
    selectedPaymentHopperId,
    selectPaymentMethod,
    product,
    isCardAllowed,
  });

  return (
    <div className={block}>
      <button
        disabled={disabled}
        className={cn("add-payment")}
        onClick={handleClickAddPaymentMethod}
      >
        <span className={cn("add-payment-content")}>
          <Icon iconName={IconNameEnum.plus} />
          {t("addPaymentMethodButton")}
        </span>
      </button>

      {savedPayments.map((savedPayment) => {
        const cardAllowed = isCardAllowed ? isCardAllowed(savedPayment) : true;
        const selectable = !cardPaymentDisabled && cardAllowed;
        return (
          <div
            className={cn("card-container", {
              selected: savedPayment.id === selectedPaymentHopperId,
              disabled: !selectable,
            })}
            key={savedPayment.id}
            onClick={
              selectable
                ? () => selectPaymentMethod(savedPayment.id)
                : undefined
            }
          >
            <CardDisplay {...savedPayment} />
            {cardAllowed && (
              <RadioGroup
                options={[{ id: savedPayment.id, value: savedPayment.id }]}
                onSelectValue={() => selectPaymentMethod(savedPayment.id)}
                selectedOption={selectedPaymentHopperId}
              />
            )}
          </div>
        );
      })}

      <MerchandisingAlert selectedPayment={selectedPayment} product={product} />
    </div>
  );
};
